import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  VALUE_TYPES,
  isEmpty,
  isValid,
  parseInput,
  formatForInput,
} from "../../utils/valueType.js";

/**
 * single_input：单值控件（6 种类型：str / int / float / bool / json / list）。
 *
 * 关键设计：内部维护 rawText 状态（用户最近一次输入的原文），与外部 value 解耦。
 * 这样切换类型时：
 *   - 用 rawText 而非 value 解析 → 保留用户输入的前导 0 / 文本细节
 *   - 转换无效（json 不接受标量等）→ 清空 + 红框消失
 *
 * - 标签由外层 action.label 承担，本组件不渲染 label。
 * - value 通过 onChange(value, valueType) 原子写回。
 *
 * 【textarea 自我回响检测】单字符 dump 修复：
 *   当用户键入 textarea（json / list）时，每个字符都会触发 onChange → 父组件更新
 *   componentData → 父组件回传新 value → 旧 useEffect 会用 formatForInput(value) 重新
 *   序列化 rawText，导致 `JSON.stringify(value)` 把字符串包装成 `"\"{\""` 之类，破坏用户输入。
 *   修复：跟踪最后一次 onChange 发送的 value（`lastSentValue`），在 useEffect 中检测到
 *   "外部 value 等于我们刚送出的值"（引用或深相等）时跳过重新格式化。
 *   调用方应在 PropertyPanel 用 `key={node.id}` 让 SingleInput 在切换节点时干净 remount。
 */
function SingleInput({
  value,
  placeholder,
  required,
  isEdit,
  valueType = "str",
  valueTypeLocked = false,
  showTypeSelector,
  onChange,
  onValueTypeChange,
}) {
  // 仅当调用方传入 onValueTypeChange 才显示类型下拉（向后兼容）
  // schema 显式声明 value_type 时（valueTypeLocked=true），下拉禁用但仍渲染
  const showType = showTypeSelector ?? typeof onValueTypeChange === "function";
  const vt = VALUE_TYPES.includes(valueType) ? valueType : "str";
  const isRequired = Boolean(required);

  // 内部 rawText：保留用户原始输入
  // - 文本类控件（str / int / float / json / list）显示与回显都用 rawText
  // - bool 控件没有文本概念，rawText 始终是 ""
  const initialText = vt === "bool" ? "" : formatForInput(value, vt);
  const [rawText, setRawText] = useState(initialText);

  // 跟踪最后一次 onChange 发送的 value（含字符串和对象形态），用于 useEffect 中的
  // 自我回响检测：父组件把同值回传时不应再用 formatForInput 重写 rawText。
  const lastSentValue = useRef(undefined);

  // 外部 value 变化时同步 rawText，但要避开以下两种"自我回响"场景：
  //   1. 我们刚发出的 value（字符串 or 解析后的对象）被父组件原样回传
  //   2. 类型切换触发的 value 变化（保留 rawText，由 handleTypeChange 自行处理）
  const prevVt = useRef(vt);
  const prevValue = useRef(value);
  useEffect(() => {
    if (prevVt.current !== vt) {
      // 类型切换触发的 value 变化：保留 rawText
      prevVt.current = vt;
      return;
    }
    if (prevValue.current !== value) {
      // 自我回响检测：value 与上次发出的值一致 → 不要用 formatForInput 重写 rawText
      if (_isSelfEcho(value, lastSentValue.current)) {
        prevValue.current = value;
        return;
      }
      // 外部 value 变化（节点切换 / 撤销重做 / 加载文件）：同步 rawText
      if (vt === "bool") {
        setRawText("");
      } else {
        setRawText(formatForInput(value, vt));
      }
      prevValue.current = value;
    }
  }, [value, vt]);

  const empty = isEmpty(value, vt);
  const showRequiredError = isRequired && empty;
  // 类型校验失败（非空且不合法）—— 用于红框
  const invalid = !empty && !isValid(value, vt);

  // 通用 onChange：value 是文本（输入框）或原生值（select / json 解析成功后）
  const handleTextChange = (text) => {
    setRawText(text);
    if (vt === "str") {
      // str 直接透传
      lastSentValue.current = text;
      onChange?.(text, vt);
      return;
    }
    if (vt === "bool") {
      // bool 不接受文本输入（应该用下拉）；兜底丢弃
      return;
    }
    if (isEmpty(text, vt)) {
      // 用户输入空白 → 置空（不解析）
      lastSentValue.current = null;
      onChange?.(null, vt);
      return;
    }
    try {
      const parsed = parseInput(text, vt);
      lastSentValue.current = parsed;
      onChange?.(parsed, vt);
    } catch (_) {
      // 解析失败：原样以字符串保留（红框），让上游 Python 校验器阻断保存
      lastSentValue.current = text;
      onChange?.(text, vt);
    }
  };

  const handleValueChange = (native) => {
    lastSentValue.current = native;
    onChange?.(native, vt);
  };

  /**
   * 切换类型：用 rawText 而非 value 做转换。
   * - 切到 str：原样保留 rawText（不归一化），保留用户输入的 "0123" 等细节
   * - 切到 bool：bool 无非法态，置空
   * - 切到 int / float / json / list：尝试 parse rawText
   *     成功 → 写入原生值
   *     失败 → 清空（**不**保留 "abc" 这类无效文本，避免用户切完类型后还看到红框旧文本）
   */
  const handleTypeChange = (newType) => {
    if (newType === vt) return;
    if (valueTypeLocked) return; // schema 锁定：业务层防御（不依赖 DOM disabled）
    onValueTypeChange?.(newType);
    if (newType === "str") {
      // 切到 str：原样保留 rawText（保留 "0123" 这类无法在 int 中表达的细节）
      lastSentValue.current = rawText;
      onChange?.(rawText, "str");
      return;
    }
    if (newType === "bool") {
      lastSentValue.current = null;
      onChange?.(null, "bool");
      return;
    }
    if (rawText === "") {
      lastSentValue.current = null;
      onChange?.(null, newType);
      return;
    }
    try {
      const parsed = parseInput(rawText, newType);
      lastSentValue.current = parsed;
      onChange?.(parsed, newType);
    } catch (_) {
      // 转换无效：清空（不要保留 "abc" 这类无效文本）
      lastSentValue.current = null;
      onChange?.(null, newType);
      setRawText("");
    }
  };

  const renderControl = () => {
    if (vt === "bool") {
      const textValue = empty ? "" : value ? "true" : "false";
      return (
        <select
          className="tfe-form-control kv-input-control"
          disabled={!isEdit}
          data-required={isRequired ? "true" : "false"}
          data-empty={showRequiredError ? "true" : "false"}
          data-invalid={invalid ? "true" : "false"}
          value={textValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") handleValueChange(null);
            else handleValueChange(v === "true");
          }}
        >
          <option value="">{"— 请选择 —"}</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    if (vt === "json" || vt === "list") {
      // json / list 共享 textarea 渲染；list 仅在解析层多做一层"必须是数组"约束
      return (
        <textarea
          className={
            "tfe-form-control kv-input-control single-input-" + vt
          }
          placeholder={placeholder ?? ""}
          readOnly={!isEdit}
          data-required={isRequired ? "true" : "false"}
          data-empty={showRequiredError ? "true" : "false"}
          data-invalid={invalid ? "true" : "false"}
          value={rawText}
          onChange={(e) => handleTextChange(e.target.value)}
        />
      );
    }

    // str / int / float：单行 input，显示用 rawText
    return (
      <input
        type="text"
        className="tfe-form-control kv-input-control"
        placeholder={placeholder ?? ""}
        value={rawText}
        readOnly={!isEdit}
        data-required={isRequired ? "true" : "false"}
        data-empty={showRequiredError ? "true" : "false"}
        data-invalid={invalid ? "true" : "false"}
        onChange={(e) => handleTextChange(e.target.value)}
      />
    );
  };

  return (
    <div className="single-input">
      <div className="single-input-row">
        <div className="single-input-control">{renderControl()}</div>
        {showType && (
          <select
            className="tfe-form-control kv-input-control tfe-single-input-type"
            disabled={!isEdit || valueTypeLocked}
            value={vt}
            title={valueTypeLocked ? "类型由 schema 声明锁定" : "字段类型"}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            {VALUE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

/**
 * 检测外部 value 是否是 SingleInput 自己刚发出的值（自我回响）。
 *
 * - 引用相等（对象 / 数组）：父组件 spread 后通常保留 componentData 引用，可直接 ===
 * - 字符串 / 原始值：直接 ===
 * - 深相等兜底：极端路径（React 18 concurrent 模式下父组件可能复刻对象）用 JSON.stringify 比对
 *   只对 list / json 这类需要避免重复 dump 的类型相关字段启用兜底
 */
function _isSelfEcho(value, lastSent) {
  if (value === lastSent) return true;
  if (value === null || lastSent === null) return value === lastSent;
  if (typeof value === "object" && typeof lastSent === "object") {
    try {
      return JSON.stringify(value) === JSON.stringify(lastSent);
    } catch (_) {
      return false;
    }
  }
  return false;
}

SingleInput.propTypes = {
  value: PropTypes.any,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  isEdit: PropTypes.bool,
  // 注：valueType 必须用 PropTypes.string 而不是 PropTypes.oneOf(VALUE_TYPES)。
  // dash-generate-components 的 generate_enum 假设 PropTypes.oneOf 的每个元素
  // 都是 {value: ...} 字典，但当数组用常量（VALUE_TYPES）传入时 react-docgen 返回
  // 的是裸字符串，导致 `v.get("value")` AttributeError。运行时仍由 valueType.js
  // 的 VALUE_TYPES + `vt = VALUE_TYPES.includes(valueType) ? valueType : "str"`
  // 做白名单校验，因此这里放宽成 string 不会损失类型安全。
  valueType: PropTypes.string,
  valueTypeLocked: PropTypes.bool,
  showTypeSelector: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onValueTypeChange: PropTypes.func,
};

export default SingleInput;